import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { ConnectionKind, ModelConstants } from 'shell-models-lib';

export const DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE = 'Cannot find entity with id {0}';

export const enum IFrameProfile {
    TridentWorkspace = 'tridentWorkspace', // Trident workspace
    MountedDataFactory = 'mountedDataFactory', // Connected data factory in Trident
    Milliman = 'milliman',
    KPMG = 'kpmg',
    KPMGWB = 'kpmgwb'
}

export interface IIFrameProfileOptions {
    disableDataFlowSource?: boolean;
    validateBeforeSave?: boolean;
    singleResourceValidate?: boolean;
    pipelineActivitySubset?: string[];
    inlineDataset?: boolean;
    referenceById?: boolean;
    dismissEmptyUserProperties?: boolean;
    tolerateInvalidReference?: boolean;
    disableDataFlowSourceOptions?: boolean;
    disableDataFlowSinkOptions?: boolean;
    disableScript?: boolean;
    showDataFlowNewSource?: boolean;
    // TODO: Remove this mapping, and let connectionTypeToLinkedServiceMapping be the single source of truth
    linkedServiceToConnectionTypeMapping?: Record<string, ConnectionKind>;
    connectionTypeToLinkedServiceMapping?: Record<ConnectionKind, string>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    preProcessResource?: (resource: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    postProcessResource?: (resource: any) => void;
}

export function convertBetweenExternalReferenceAndLinkedServiceReference(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    properties: any,
    fromExternalToLs: boolean,
    lsObjectId?: string,
    artifactType: string = undefined
): void {
    const externalReferencesKey = 'externalReferences';
    const rejectedDataExternalReferencesKey = 'rejectedDataExternalReferences';
    const schemaExternalReferencesKey = 'schemaExternalReferences';
    const landingZoneExternalReferencesKey = 'landingZoneExternalReferences';

    if (properties && typeof properties === 'object') {
        if (Array.isArray(properties)) {
            properties.forEach((val) =>
                convertBetweenExternalReferenceAndLinkedServiceReference(val, fromExternalToLs, lsObjectId, artifactType)
            );
        } else {
            Object.keys(properties).forEach((key) => {
                if ((key === externalReferencesKey ||
                     key === rejectedDataExternalReferencesKey ||
                     key === schemaExternalReferencesKey ||
                     key === landingZoneExternalReferencesKey) &&
                    fromExternalToLs &&
                    properties[key]?.connection) {
                    const linkedServiceReference = {
                        type: 'LinkedServiceReference',
                        referenceName: properties[key].connection,
                        referenceType: properties[key].connectionType
                    };
                    if (Object.keys(properties[key]).length === 1) {
                        delete properties[key];
                    }
                    let lsKey: string;
                    switch (artifactType) {
                        case ModelConstants.scopeActivity:
                            lsKey = 'scriptLinkedService';
                            break;
                        case ModelConstants.customActivity:
                            lsKey = 'resourceLinkedService';
                            break;
                        case ModelConstants.azureHDInsightActivity:
                            lsKey = 'fileConnection';
                            break;
                        case ModelConstants.mappingDataflowActivity:
                            {
                                lsKey = 'linkedService';
                                if (key === rejectedDataExternalReferencesKey) {
                                    lsKey = 'rejectedDataLinkedService';
                                } else if (key === schemaExternalReferencesKey) {
                                    lsKey = 'schemaLinkedService';
                                } else if (key === landingZoneExternalReferencesKey) {
                                    lsKey = 'landingZoneLinkedService';
                                }
                            }
                            break;
                        default:
                            lsKey = 'linkedServiceName';
                            break;
                    }
                    properties[lsKey] = linkedServiceReference;
                } else if (
                    (key === 'linkedServiceName' ||
                        key === 'scriptLinkedService' ||
                        key === 'resourceLinkedService' ||
                        key === 'fileConnection' ||
                        key === 'linkedService' ||
                        key === 'rejectedDataLinkedService' ||
                        key === 'schemaLinkedService' ||
                        key === 'landingZoneLinkedService') &&
                    !fromExternalToLs &&
                    properties[key]?.referenceName
                ) {
                    let extRefKey = externalReferencesKey;
                    if (key === 'rejectedDataLinkedService') {
                        extRefKey = rejectedDataExternalReferencesKey;
                    } else if (key === 'schemaLinkedService') {
                        extRefKey = schemaExternalReferencesKey;
                    } else if (key === 'landingZoneLinkedService') {
                        extRefKey = landingZoneExternalReferencesKey;
                    }
                    properties[extRefKey] = properties[extRefKey] || { connection: '' };
                    properties[extRefKey].connection = lsObjectId
                        ? lsObjectId
                        : properties[key].referenceName;
                    properties[extRefKey].connectionType = properties[key].referenceType;
                    delete properties[key];
                } else {
                    if (key === 'typeProperties' && fromExternalToLs) {
                        artifactType = properties['type']?.toString();
                    }

                    convertBetweenExternalReferenceAndLinkedServiceReference(
                        properties[key],
                        fromExternalToLs,
                        lsObjectId,
                        artifactType
                    );

                    if (key === 'typeProperties' && fromExternalToLs) {
                        artifactType = undefined;
                    }
                }
            });
        }
    }
}

export const iFrameCustomerProfiles: { [key: string]: IIFrameProfileOptions } = {
    [IFrameProfile.TridentWorkspace]: {
        inlineDataset: true,
        referenceById: true,
        dismissEmptyUserProperties: true,
        tolerateInvalidReference: true,
        showDataFlowNewSource: true,
        validateBeforeSave: true,
        singleResourceValidate: true,
        linkedServiceToConnectionTypeMapping: {
            [adfModelTypeMap.azureBlobFS.linkedServiceType]: ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN2,
            [adfModelTypeMap.azureBlob.linkedServiceType]: ConnectionKind.AZURE_BLOBS,
            [adfModelTypeMap.azureDataExplorer.linkedServiceType]: ConnectionKind.AZURE_DATA_EXPLORER,
            [adfModelTypeMap.sqlServer.linkedServiceType]: ConnectionKind.SQL,
            [adfModelTypeMap.azureSql.linkedServiceType]: ConnectionKind.SQL,
            [adfModelTypeMap.azureSqlDW.linkedServiceType]: ConnectionKind.SQL,
            [adfModelTypeMap.azureSqlMI.linkedServiceType]: ConnectionKind.AZURE_SQL_MI,
            [adfModelTypeMap.amazonRdsSqlServer.linkedServiceType]: ConnectionKind.SQL,
            [adfModelTypeMap.amazonRdsForOracle.linkedServiceType]: ConnectionKind.AMAZON_RDS_FOR_ORACLE,
            [adfModelTypeMap.cosmosSql.linkedServiceType]: ConnectionKind.AZURE_COSMOS_DB_NOSQL_V2,
            [adfModelTypeMap.azureTable.linkedServiceType]: ConnectionKind.AZURE_TABLE_STORAGE,
            [adfModelTypeMap.amazonS3.linkedServiceType]: ConnectionKind.AMAZON_S3,
            [adfModelTypeMap.amazonS3Compatible.linkedServiceType]: ConnectionKind.AMAZON_S3,
            [adfModelTypeMap.amazonRedshift.linkedServiceType]: ConnectionKind.AMAZON_REDSHIFT,
            [adfModelTypeMap.odata.linkedServiceType]: ConnectionKind.ODATA,
            [adfModelTypeMap.googleCloudStorage.linkedServiceType]: ConnectionKind.GOOGLE_CLOUD_STORAGE,
            [adfModelTypeMap.impala.linkedServiceType]: ConnectionKind.IMPALA,
            [adfModelTypeMap.spark.linkedServiceType]: ConnectionKind.SPARK,
            [adfModelTypeMap.azureDatabricks.linkedServiceType]: ConnectionKind.AZURE_DATABRICKS_WORKSPACE,
            [adfModelTypeMap.hive.linkedServiceType]: ConnectionKind.HIVE,
            [adfModelTypeMap.snowflake.linkedServiceType]: ConnectionKind.SNOWFLAKE,
            [adfModelTypeMap.commonDataService.linkedServiceType]: ConnectionKind.DATAVERSE,
            [adfModelTypeMap.dynamicsCRM.linkedServiceType]: ConnectionKind.DATAVERSE,
            [adfModelTypeMap.dynamicsAX.linkedServiceType]: ConnectionKind.DATAVERSE,
            [adfModelTypeMap.dynamics.linkedServiceType]: ConnectionKind.DATAVERSE,
            [adfModelTypeMap.sharePointOnlineList.linkedServiceType]: ConnectionKind.SHAREPOINT,
            [adfModelTypeMap.sharePointOnlineFile.linkedServiceType]: ConnectionKind.SHAREPOINT,
            [adfModelTypeMap.azureDataLakeStore.linkedServiceType]: ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN1,
            [adfModelTypeMap.postgreSql.linkedServiceType]: ConnectionKind.POSTGRE_SQL,
            [adfModelTypeMap.azurePostgreSQL.linkedServiceType]: ConnectionKind.POSTGRE_SQL, // Keep this as we rely on it for n:1 mapping. Todo, improve this part.
            [adfModelTypeMap.azureHorizonDb.linkedServiceType]: ConnectionKind.AZURE_HORIZON_DB,
            [adfModelTypeMap.office365.linkedServiceType]: ConnectionKind.MICROSOFT365,
            [adfModelTypeMap.mysql.linkedServiceType]: ConnectionKind.MYSQL,
            [adfModelTypeMap.sapCdc.linkedServiceType]: ConnectionKind.SAPCDC,
            [adfModelTypeMap.sapBdc.linkedServiceType]: ConnectionKind.SAP_BUSINESS_DATA_CLOUD,
            [adfModelTypeMap.azureMySql.linkedServiceType]: ConnectionKind.AZURE_DATABASE_FOR_MYSQL,
            [adfModelTypeMap.mariaDB.linkedServiceType]: ConnectionKind.MariaDB,
            [adfModelTypeMap.salesforce.linkedServiceType]: ConnectionKind.SALESFORCE,
            [adfModelTypeMap.salesforceServiceCloudV2.linkedServiceType]: ConnectionKind.SALESFORCE_SERVICE_CLOUD,
            /**
             * In order to make IframeService.isMappingToMultipleConnectors returns true for ConnectionKind.WEB.
             * Will refactor when deprecating linkedServiceToConnectionTypeMapping
             */
            [adfModelTypeMap.rest.linkedServiceType]: ConnectionKind.WEB,
            [adfModelTypeMap.httpServer.linkedServiceType]: ConnectionKind.WEB,
            [adfModelTypeMap.googleBigQuery.linkedServiceType]: ConnectionKind.GOOGLE_BIG_QUERY,
            [adfModelTypeMap.azureDataLakeAnalytics.linkedServiceType]: ConnectionKind.AZURE_DATA_LAKE_ANALYTICS,
            [adfModelTypeMap.databricks.linkedServiceType]: ConnectionKind.DATABRICKS_MULTI_CLOUD,
            [adfModelTypeMap.azureDatabricksDeltaLake.linkedServiceType]: ConnectionKind.DATABRICKS,
            [adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType]:
                ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN1,
            [adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType]:
                ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN2,
            [adfModelTypeMap.webActivity.linkedServiceType]: ConnectionKind.WEB_FOR_PIPELINE,
            [adfModelTypeMap.azureFunction.linkedServiceType]: ConnectionKind.AZURE_FUNCTION,
            [adfModelTypeMap.amlService.linkedServiceType]: ConnectionKind.AZURE_MACHINE_LEARNING,
            [adfModelTypeMap.odbc.linkedServiceType]: ConnectionKind.ODBC,
            [adfModelTypeMap.db2.linkedServiceType]: ConnectionKind.DB2,
            [adfModelTypeMap.fileServer.linkedServiceType]: ConnectionKind.FileSever,
            [adfModelTypeMap.azureBatch.linkedServiceType]: ConnectionKind.AZURE_BATCH,
            [adfModelTypeMap.mongoDb.linkedServiceType]: ConnectionKind.MONGODB,
            [adfModelTypeMap.mongoDbAtlas.linkedServiceType]: ConnectionKind.MONGODB_ATLAS,
            [adfModelTypeMap.hdfs.linkedServiceType]: ConnectionKind.HDFS,
            [adfModelTypeMap.cosmosMongo.linkedServiceType]: ConnectionKind.AZURE_COSMOSDB_FOR_MONGODB,
            [adfModelTypeMap.ftpServer.linkedServiceType]: ConnectionKind.FTP,
            [adfModelTypeMap.sftp.linkedServiceType]: ConnectionKind.SFTP,
            [adfModelTypeMap.sapHana.linkedServiceType]: ConnectionKind.SAP_HANA,
            [adfModelTypeMap.sapTable.linkedServiceType]: ConnectionKind.SAP_TABLE_MESSAGE_SERVER,
            [adfModelTypeMap.sapOpenHub.linkedServiceType]: ConnectionKind.SAP_BW_OPEN_HUB_MESSAGE_SERVER,
            [adfModelTypeMap.sapDatasphereForAzureDataLakeStorageGen2.linkedServiceType]: ConnectionKind.SAP_AZURE_DATA_LAKE_STORAGE_GEN2,
            [adfModelTypeMap.oneLakeFile.linkedServiceType]: ConnectionKind.ONE_LAKE_FILE,
            [adfModelTypeMap.sapDatasphereForGoogleCloudStorage.linkedServiceType]: ConnectionKind.SAP_DATASPHERE_GOOGLE_CLOUD_STORAGE,
            [adfModelTypeMap.sapDatasphereForAmazonS3.linkedServiceType]: ConnectionKind.SAP_DATASPHERE_AMAZON_S3,
            [adfModelTypeMap.powerBIDatasets.linkedServiceType]: ConnectionKind.POWERBI_DATASETS,
            [adfModelTypeMap.azureKeyVault.linkedServiceType]: ConnectionKind.AZURE_KEY_VAULT,
            [adfModelTypeMap.azureDataFactory.linkedServiceType]: ConnectionKind.AZURE_DATA_FACTORY,
            [adfModelTypeMap.azureSynapseAnalytics.linkedServiceType]: ConnectionKind.AZURE_SYNAPSE_WORKSPACE,
            [adfModelTypeMap.oracle.linkedServiceType]: ConnectionKind.ORACLE,
            [adfModelTypeMap.oracleCloudStorage.linkedServiceType]: ConnectionKind.ORACLE_CLOUD_STORAGE,
            [adfModelTypeMap.fabricDataPipelines.linkedServiceType]: ConnectionKind.FABRIC_DATA_PIPELINES,
            [adfModelTypeMap.azureSearch.linkedServiceType]: ConnectionKind.AZURE_AI_SEARCH,
            [adfModelTypeMap.azureFileStorage.linkedServiceType]: ConnectionKind.AZURE_FILE,
            [adfModelTypeMap.hdInsight.linkedServiceType]: ConnectionKind.AZURE_HDINSIGHT_CLUSTER,
            [adfModelTypeMap.hDInsightOnDemand.linkedServiceType]: ConnectionKind.AZURE_HDINSIGHT_ON_DEMAND,
            [adfModelTypeMap.microsoftTeams.linkedServiceType]: ConnectionKind.MICROSOFT_TEAMS,
            [adfModelTypeMap.office365Email.linkedServiceType]: ConnectionKind.MICROSOFT_OUTLOOK,
            [adfModelTypeMap.vertica.linkedServiceType]: ConnectionKind.VERTICA,
            [adfModelTypeMap.gitHub.linkedServiceType]: ConnectionKind.GITHUB_SOURCE_CONTROL,
            [adfModelTypeMap.serviceNowV2.linkedServiceType]: ConnectionKind.SERVICE_NOW,
            [adfModelTypeMap.tridentSparkJobDefinition.linkedServiceType]: ConnectionKind.SPARK_JOB_DEFINITION,
            [adfModelTypeMap.fabricNotebook.linkedServiceType]: ConnectionKind.NOTEBOOK,
            [adfModelTypeMap.fabricAirflow.linkedServiceType]: ConnectionKind.FABRIC_AIRFLOW,
            [adfModelTypeMap.fabricUserDataFunctions.linkedServiceType]: ConnectionKind.USER_DATA_FUNCTIONS,
            [adfModelTypeMap.teradata.linkedServiceType]: ConnectionKind.Teradata,
            [adfModelTypeMap.fabricDataflow.linkedServiceType]: ConnectionKind.DATAFLOW,
            [adfModelTypeMap.greenplum.linkedServiceType]: ConnectionKind.GREENPLUM,
            [adfModelTypeMap.invokeCopyJob.linkedServiceType]: ConnectionKind.COPY_JOB,
            [adfModelTypeMap.presto.linkedServiceType]: ConnectionKind.PRESTO,
            [adfModelTypeMap.informix.linkedServiceType]: ConnectionKind.INFORMIX,
            [adfModelTypeMap.cassandra.linkedServiceType]: ConnectionKind.CASSANDRA,
            [adfModelTypeMap.jira.linkedServiceType]: ConnectionKind.JIRA,
            [adfModelTypeMap.attio.linkedServiceType]: ConnectionKind.ATTIO,
            [adfModelTypeMap.bitbucket.linkedServiceType]: ConnectionKind.BITBUCKET,
            [adfModelTypeMap.oracleFusion.linkedServiceType]: ConnectionKind.ORACLE_FUSION,
            [adfModelTypeMap.microsoftAccess.linkedServiceType]: ConnectionKind.MICROSOFT_ACCESS,
            [adfModelTypeMap.warehouseCredential.linkedServiceType]: ConnectionKind.WARHOUSE,
            [adfModelTypeMap.lakehouseCredential.linkedServiceType]: ConnectionKind.LAKEHOUSE,
            [adfModelTypeMap.sqlAnalyticsEndpointCredential.linkedServiceType]: ConnectionKind.SQL_ANALYTICS_ENDPOINT,
            [adfModelTypeMap.fabricSqlCredential.linkedServiceType]: ConnectionKind.FABRIC_SQL,
            [adfModelTypeMap.eventStreamCredential.linkedServiceType]: ConnectionKind.EVENT_STREAM,
            [adfModelTypeMap.quickBooks.linkedServiceType]: ConnectionKind.QUICKBOOKS,
            [adfModelTypeMap.shopify.linkedServiceType]: ConnectionKind.SHOPIFY,
            [adfModelTypeMap.netezza.linkedServiceType]: ConnectionKind.NETEZZA,
            [adfModelTypeMap.fabricSqlEndpoint.linkedServiceType]: ConnectionKind.FABRIC_SQL_ENDPOINT_METADATA,
            [adfModelTypeMap.invokeDataBuildToolJob.linkedServiceType]: ConnectionKind.DATA_BUILD_TOOL_JOB,
            [adfModelTypeMap.vivaInsights.linkedServiceType]: ConnectionKind.VIVA_INSIGHTS,
            [adfModelTypeMap.invokeDataQuality.linkedServiceType]: ConnectionKind.DATA_QUALITY,
        },
        connectionTypeToLinkedServiceMapping: {
            [ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN2]: adfModelTypeMap.azureBlobFS.linkedServiceType,
            [ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN2_COSMOS_STRUCTURED_STREAM]: adfModelTypeMap.azureDataLakeStoreGen2CosmosStructuredStream.linkedServiceType,
            [ConnectionKind.AZURE_BLOBS]: adfModelTypeMap.azureBlob.linkedServiceType,
            [ConnectionKind.AZURE_BLOBS_COSMOS_STRUCTURED_STREAM]: adfModelTypeMap.azureDataLakeStoreCosmosStructuredStream.linkedServiceType,
            [ConnectionKind.AZURE_DATA_EXPLORER]: adfModelTypeMap.azureDataExplorer.linkedServiceType,
            [ConnectionKind.SQL]: adfModelTypeMap.azureSql.linkedServiceType,
            [ConnectionKind.AZURE_SQL_MI]: adfModelTypeMap.azureSqlMI.linkedServiceType,
            [ConnectionKind.AMAZON_RDS_FOR_SQL_SERVER]: adfModelTypeMap.amazonRdsSqlServer.linkedServiceType,
            [ConnectionKind.AMAZON_RDS_FOR_ORACLE]: adfModelTypeMap.amazonRdsForOracle.linkedServiceType,
            [ConnectionKind.AZURE_DATA_PIPELINE_COSMOS_DB]: adfModelTypeMap.cosmosSql.linkedServiceType,
            [ConnectionKind.AZURE_COSMOS_DB_NOSQL_V2]: adfModelTypeMap.cosmosSql.linkedServiceType,
            [ConnectionKind.AZURE_TABLE_STORAGE]: adfModelTypeMap.azureTable.linkedServiceType,
            [ConnectionKind.AMAZON_S3]: adfModelTypeMap.amazonS3.linkedServiceType,
            [ConnectionKind.AMAZON_S3_COMPATIBLE]: adfModelTypeMap.amazonS3Compatible.linkedServiceType,
            [ConnectionKind.AMAZON_REDSHIFT]: adfModelTypeMap.amazonRedshift.linkedServiceType,
            [ConnectionKind.ODATA]: adfModelTypeMap.odata.linkedServiceType,
            [ConnectionKind.GOOGLE_CLOUD_STORAGE]: adfModelTypeMap.googleCloudStorage.linkedServiceType,
            [ConnectionKind.IMPALA]: adfModelTypeMap.impala.linkedServiceType,
            [ConnectionKind.SPARK]: adfModelTypeMap.spark.linkedServiceType,
            [ConnectionKind.AZURE_DATABRICKS_WORKSPACE]: adfModelTypeMap.azureDatabricks.linkedServiceType,
            [ConnectionKind.HIVE]: adfModelTypeMap.hive.linkedServiceType,
            [ConnectionKind.SNOWFLAKE]: adfModelTypeMap.snowflake.linkedServiceType,
            [ConnectionKind.DATAVERSE]: adfModelTypeMap.commonDataService.linkedServiceType,
            [ConnectionKind.DYNAMICS_365]: adfModelTypeMap.dynamics.linkedServiceType,
            [ConnectionKind.DYNAMICS_CRM]: adfModelTypeMap.dynamicsCRM.linkedServiceType,
            [ConnectionKind.DYNAMICS_AX]: adfModelTypeMap.dynamicsAX.linkedServiceType,
            [ConnectionKind.SHAREPOINT]: adfModelTypeMap.sharePointOnlineList.linkedServiceType,
            [ConnectionKind.AZURE_DATA_LAKE_STORAGE_GEN1]: adfModelTypeMap.azureDataLakeStore.linkedServiceType,
            [ConnectionKind.MICROSOFT365]: adfModelTypeMap.office365.linkedServiceType,
            [ConnectionKind.POSTGRE_SQL]: adfModelTypeMap.postgreSql.linkedServiceType,
            [ConnectionKind.AZURE_POSTGRE_SQL]: adfModelTypeMap.azurePostgreSQL.linkedServiceType,
            [ConnectionKind.AZURE_HORIZON_DB]: adfModelTypeMap.azureHorizonDb.linkedServiceType,
            [ConnectionKind.WEB]: adfModelTypeMap.rest.linkedServiceType,
            [ConnectionKind.WEB_FOR_PIPELINE]: adfModelTypeMap.rest.linkedServiceType,
            [ConnectionKind.SAPCDC]: adfModelTypeMap.sapCdc.linkedServiceType,
            [ConnectionKind.SAP_BUSINESS_DATA_CLOUD]: adfModelTypeMap.sapBdc.linkedServiceType,
            [ConnectionKind.REST_SERVICE]: adfModelTypeMap.rest.linkedServiceType,
            [ConnectionKind.HTTP_SERVER]: adfModelTypeMap.httpServer.linkedServiceType,
            [ConnectionKind.GOOGLE_BIG_QUERY]: adfModelTypeMap.googleBigQuery.linkedServiceType,
            [ConnectionKind.AZURE_DATA_LAKE_ANALYTICS]: adfModelTypeMap.azureDataLakeAnalytics.linkedServiceType,
            [ConnectionKind.DATABRICKS]: adfModelTypeMap.azureDatabricksDeltaLake.linkedServiceType,
            [ConnectionKind.DATABRICKS_MULTI_CLOUD]: adfModelTypeMap.databricks.linkedServiceType,
            [ConnectionKind.AZURE_FUNCTION]: adfModelTypeMap.azureFunction.linkedServiceType,
            [ConnectionKind.AZURE_MACHINE_LEARNING]: adfModelTypeMap.amlService.linkedServiceType,
            [ConnectionKind.AZURE_BATCH]: adfModelTypeMap.azureBatch.linkedServiceType,
            [ConnectionKind.MONGODB]: adfModelTypeMap.mongoDb.linkedServiceType,
            [ConnectionKind.MONGODB_ATLAS]: adfModelTypeMap.mongoDbAtlas.linkedServiceType,
            [ConnectionKind.HDFS]: adfModelTypeMap.hdfs.linkedServiceType,
            [ConnectionKind.AZURE_COSMOSDB_FOR_MONGODB]: adfModelTypeMap.cosmosMongo.linkedServiceType,
            [ConnectionKind.SAP_HANA]: adfModelTypeMap.sapHana.linkedServiceType,
            [ConnectionKind.SAP_TABLE_MESSAGE_SERVER]: adfModelTypeMap.sapTable.linkedServiceType,
            [ConnectionKind.SAP_TABLE_APPLICATION_SERVER]: adfModelTypeMap.sapTable.linkedServiceType,
            [ConnectionKind.SAP_BW_OPEN_HUB_MESSAGE_SERVER]: adfModelTypeMap.sapOpenHub.linkedServiceType,
            [ConnectionKind.SAP_BW_OPEN_HUB_APPLICATION_SERVER]: adfModelTypeMap.sapOpenHub.linkedServiceType,
            [ConnectionKind.SAP_AZURE_DATA_LAKE_STORAGE_GEN2]: adfModelTypeMap.sapDatasphereForAzureDataLakeStorageGen2.linkedServiceType,
            [ConnectionKind.ONE_LAKE_FILE]: adfModelTypeMap.oneLakeFile.linkedServiceType,
            [ConnectionKind.SAP_DATASPHERE_GOOGLE_CLOUD_STORAGE]: adfModelTypeMap.sapDatasphereForGoogleCloudStorage.linkedServiceType,
            [ConnectionKind.SAP_DATASPHERE_AMAZON_S3]: adfModelTypeMap.sapDatasphereForAmazonS3.linkedServiceType,
            [ConnectionKind.ODBC]: adfModelTypeMap.odbc.linkedServiceType,
            [ConnectionKind.FileSever]: adfModelTypeMap.fileServer.linkedServiceType,
            [ConnectionKind.DB2]: adfModelTypeMap.db2.linkedServiceType,
            [ConnectionKind.SFTP]: adfModelTypeMap.sftp.linkedServiceType,
            [ConnectionKind.FTP]: adfModelTypeMap.ftpServer.linkedServiceType,
            [ConnectionKind.POWERBI_DATASETS]: adfModelTypeMap.powerBIDatasets.linkedServiceType,
            [ConnectionKind.AZURE_KEY_VAULT]: adfModelTypeMap.azureKeyVault.linkedServiceType,
            [ConnectionKind.AZURE_DATA_FACTORY]: adfModelTypeMap.azureDataFactory.linkedServiceType,
            [ConnectionKind.AZURE_SYNAPSE_WORKSPACE]: adfModelTypeMap.azureSynapseAnalytics.linkedServiceType,
            [ConnectionKind.ORACLE]: adfModelTypeMap.oracle.linkedServiceType,
            [ConnectionKind.ORACLE_CLOUD_STORAGE]: adfModelTypeMap.oracleCloudStorage.linkedServiceType,
            [ConnectionKind.FABRIC_DATA_PIPELINES]: adfModelTypeMap.fabricDataPipelines.linkedServiceType,
            [ConnectionKind.AZURE_AI_SEARCH]: adfModelTypeMap.azureSearch.linkedServiceType,
            [ConnectionKind.MYSQL]: adfModelTypeMap.mysql.linkedServiceType,
            [ConnectionKind.AZURE_DATABASE_FOR_MYSQL]: adfModelTypeMap.azureMySql.linkedServiceType,
            [ConnectionKind.MariaDB]: adfModelTypeMap.mariaDB.linkedServiceType,
            [ConnectionKind.AZURE_FILE]: adfModelTypeMap.azureFileStorage.linkedServiceType,
            [ConnectionKind.AZURE_HDINSIGHT_CLUSTER]: adfModelTypeMap.hdInsight.linkedServiceType,
            [ConnectionKind.AZURE_HDINSIGHT_ON_DEMAND]: adfModelTypeMap.hDInsightOnDemand.linkedServiceType,
            [ConnectionKind.MICROSOFT_TEAMS]: adfModelTypeMap.microsoftTeams.linkedServiceType,
            [ConnectionKind.MICROSOFT_OUTLOOK]: adfModelTypeMap.office365Email.linkedServiceType,
            [ConnectionKind.SALESFORCE]: adfModelTypeMap.salesforce.linkedServiceType,
            [ConnectionKind.SALESFORCE_SERVICE_CLOUD]: adfModelTypeMap.salesforceServiceCloudV2.linkedServiceType,
            [ConnectionKind.VERTICA]: adfModelTypeMap.vertica.linkedServiceType,
            [ConnectionKind.GITHUB_SOURCE_CONTROL]: adfModelTypeMap.gitHub.linkedServiceType,
            [ConnectionKind.SERVICE_NOW]: adfModelTypeMap.serviceNowV2.linkedServiceType,
            [ConnectionKind.SPARK_JOB_DEFINITION]: adfModelTypeMap.tridentSparkJobDefinition.linkedServiceType,
            [ConnectionKind.NOTEBOOK]: adfModelTypeMap.fabricNotebook.linkedServiceType,
            [ConnectionKind.FABRIC_AIRFLOW]: adfModelTypeMap.fabricAirflow.linkedServiceType,
            [ConnectionKind.USER_DATA_FUNCTIONS]: adfModelTypeMap.fabricUserDataFunctions.linkedServiceType,
            [ConnectionKind.FABRIC_SQL]: adfModelTypeMap.fabricSqlCredential.linkedServiceType,
            [ConnectionKind.WARHOUSE]: adfModelTypeMap.warehouseCredential.linkedServiceType,
            [ConnectionKind.LAKEHOUSE]: adfModelTypeMap.lakehouseCredential.linkedServiceType,
            [ConnectionKind.Teradata]: adfModelTypeMap.teradata.linkedServiceType,
            [ConnectionKind.DATAFLOW]: adfModelTypeMap.fabricDataflow.linkedServiceType,
            [ConnectionKind.GREENPLUM]: adfModelTypeMap.greenplum.linkedServiceType,
            [ConnectionKind.COPY_JOB]: adfModelTypeMap.invokeCopyJob.linkedServiceType,
            [ConnectionKind.INFORMIX]: adfModelTypeMap.informix.linkedServiceType,
            [ConnectionKind.PRESTO]: adfModelTypeMap.presto.linkedServiceType,
            [ConnectionKind.CASSANDRA]: adfModelTypeMap.cassandra.linkedServiceType,
            [ConnectionKind.JIRA]: adfModelTypeMap.jira.linkedServiceType,
            [ConnectionKind.VIVA_INSIGHTS]: adfModelTypeMap.vivaInsights.linkedServiceType,
            [ConnectionKind.ATTIO]: adfModelTypeMap.attio.linkedServiceType,
            [ConnectionKind.BITBUCKET]: adfModelTypeMap.bitbucket.linkedServiceType,
            [ConnectionKind.ORACLE_FUSION]: adfModelTypeMap.oracleFusion.linkedServiceType,
            [ConnectionKind.SQL_ANALYTICS_ENDPOINT]: adfModelTypeMap.sqlAnalyticsEndpointCredential.linkedServiceType,
            [ConnectionKind.FABRIC_MATERIALIZED_LAKEHOUSE_VIEW]: adfModelTypeMap.fabricMaterializedLakehouseView.linkedServiceType,
            [ConnectionKind.MICROSOFT_ACCESS]: adfModelTypeMap.microsoftAccess.linkedServiceType,
            [ConnectionKind.QUICKBOOKS]: adfModelTypeMap.quickBooks.linkedServiceType,
            [ConnectionKind.SHOPIFY]: adfModelTypeMap.shopify.linkedServiceType,
            [ConnectionKind.NETEZZA]: adfModelTypeMap.netezza.linkedServiceType,
            [ConnectionKind.FABRIC_SQL_ENDPOINT_METADATA]: adfModelTypeMap.fabricSqlEndpoint.linkedServiceType,
            [ConnectionKind.EVENT_STREAM]: adfModelTypeMap.eventStreamCredential.linkedServiceType,
            [ConnectionKind.DATA_BUILD_TOOL_JOB]: adfModelTypeMap.invokeDataBuildToolJob.linkedServiceType,
            [ConnectionKind.DATA_QUALITY]: adfModelTypeMap.invokeDataQuality.linkedServiceType,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        preProcessResource: (resource: any) => convertBetweenExternalReferenceAndLinkedServiceReference(resource, true),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        postProcessResource: (resource: any) =>
            convertBetweenExternalReferenceAndLinkedServiceReference(resource, false)
    },
    [IFrameProfile.MountedDataFactory]: {
        disableDataFlowSourceOptions: false,
        disableDataFlowSinkOptions: false
    },
    [IFrameProfile.Milliman]: {},
    [IFrameProfile.KPMG]: {
        disableDataFlowSource: true,
        validateBeforeSave: true,
        singleResourceValidate: true
    },
    [IFrameProfile.KPMGWB]: {
        disableDataFlowSourceOptions: true,
        disableDataFlowSinkOptions: true,
        disableScript: true,
        validateBeforeSave: true,
        singleResourceValidate: true
    }
};
